import {
  AccountTitle,
  AssetClass,
  AssetEventType,
  AssetStatus,
  DisposalMethod,
  InventoryResult,
  InventoryStatus,
  Tangibility,
  enumKeyOf,
  formatMoney,
} from '@mai-team-amusement/core';
import { useCommonT, useLocaleState } from '@mai-team-amusement/ui';
import { DateTime } from 'luxon';
import { useMemo } from 'react';

/** Labels of the fixed-asset enums in the UI language (from the common namespace). */
export function useLabels() {
  const common = useCommonT();
  const { locale } = useLocaleState();
  return useMemo(
    () => ({
      status: (value: AssetStatus) => common(`enum.AssetStatus.${enumKeyOf(AssetStatus, value)}`),
      accountTitle: (value: AccountTitle) =>
        common(`enum.AccountTitle.${enumKeyOf(AccountTitle, value)}`),
      assetClass: (value: AssetClass) => common(`enum.AssetClass.${enumKeyOf(AssetClass, value)}`),
      tangibility: (value: Tangibility) =>
        common(`enum.Tangibility.${enumKeyOf(Tangibility, value)}`),
      disposalMethod: (value: DisposalMethod) =>
        common(`enum.DisposalMethod.${enumKeyOf(DisposalMethod, value)}`),
      eventType: (value: AssetEventType) =>
        common(`enum.AssetEventType.${enumKeyOf(AssetEventType, value)}`),
      inventoryStatus: (value: InventoryStatus) =>
        common(`enum.InventoryStatus.${enumKeyOf(InventoryStatus, value)}`),
      inventoryResult: (value: InventoryResult) =>
        common(`enum.InventoryResult.${enumKeyOf(InventoryResult, value)}`),
      money: (amount: string, currency: string) => formatMoney(amount, currency, locale),
      /** `2026/10/21` (dates are calendar dates, shown the same in every language). */
      date: (iso: string) => DateTime.fromISO(iso).toFormat('yyyy/MM/dd'),
      /** The country's name in the UI language. */
      country: (code: string) =>
        new Intl.DisplayNames([locale], { type: 'region' }).of(code) ?? code,
    }),
    [common, locale],
  );
}

export type Labels = ReturnType<typeof useLabels>;
